import { Link } from "@remix-run/react";
import { CREDENTIAL_SIGNATURE_SVG, credentialPaperText, formatCertificateCode } from "@repo/shared";
import { Check, Copy, ExternalLink } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState } from "react";

import academiaIsotipo from "~/assets/academia-isotipo.png";
import { cn } from "~/lib/utils";
import { credentialPublicPath } from "~/types/certificate";

import type { CredentialType } from "@repo/shared";
import type { CSSProperties } from "react";

/**
 * Los tokens `landing-*` con sus valores CLAROS. La tarjeta pinta con colores fijos
 * (`styles/certificate.css`), pero lo que se monte adentro con clases de la landing tiene que
 * verse como en modo claro también de noche: un certificado es papel, y su tinta es tinta.
 */
export const PAPEL_CLARO = {
  "--landing-stone-50": "253 251 247",
  "--landing-stone-100": "251 248 242",
  "--landing-stone-200": "245 240 228",
  "--landing-stone-300": "229 223 211",
  "--landing-stone-400": "200 192 178",
  "--landing-stone-500": "160 151 137",
  "--landing-stone-600": "112 104 94",
  "--landing-stone-700": "87 79 71",
  "--landing-stone-800": "54 47 42",
  "--landing-stone-900": "31 26 21",
  "--landing-red-50": "254 242 240",
  "--landing-red-200": "242 184 176",
  "--landing-red-400": "192 57 43",
  "--landing-red-500": "169 50 38",
  "--landing-success": "53 122 80",
  "--landing-white": "255 255 255",
} as CSSProperties;

/**
 * - `full`: la credencial para leer. Ancha, es la réplica del PDF; angosta, se apila.
 * - `thumbnail`: miniatura para listas. SIEMPRE la réplica, a la escala que toque. No es para
 *   leer: es para reconocerla y tocarla.
 * - `example`: como `full`, con marca de agua EJEMPLO. Para las landings, con datos de muestra.
 */
export type CertificateCardVariant = "full" | "thumbnail" | "example";

/**
 * Qué acompaña al código: copiar + link a la página pública (`full`), sólo copiar (dentro de
 * la propia página pública), o nada (miniaturas, ejemplos: ahí va dónde se verifica).
 */
export type CertificateCodeActions = "full" | "copy" | "none";

export type CertificateCardProps = {
  holderName: string;
  courseTitle: string;
  type: CredentialType | null | undefined;
  completedAt?: string | null;
  issuedAt?: string | null;
  code?: string | null;
  variant?: CertificateCardVariant;
  codeActions?: CertificateCodeActions;
  className?: string;
};

/**
 * Cuerpo del nombre y del curso según el largo: la misma estimación que hace el servidor
 * antes de medir (`fitFontSize` de la plantilla del PDF), con sus mismos topes. En la réplica
 * va en milímetros del A4; en la apilada, en px de teléfono.
 */
function fitSize(length: number, max: number, min: number, comfortable: number): number {
  if (length <= comfortable) return max;
  return Math.max(min, Math.round(max * (comfortable / length) * 2) / 2);
}
const PT_TO_MM = 0.3528;

/** Mismo `verifyHint` que el papel: el host de donde se está mirando, ruta `/verificar`. */
const FALLBACK_HOST = "cursosacademia.ar";

/**
 * El origen de la página, sólo después de montar: en el servidor no hay `window`, y leerlo en
 * el render rompería la hidratación de la página pública (que es SSR).
 */
function useOrigin(): string | null {
  const [origin, setOrigin] = useState<string | null>(null);
  useEffect(() => setOrigin(window.location.origin), []);
  return origin;
}

function CopyCodeButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = async () => {
    try {
      // La forma canónica, sin separadores: es la que se pega en cualquier buscador.
      await navigator.clipboard.writeText(code);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      // Sin permiso de portapapeles el código igual queda visible y seleccionable a mano.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? "Código copiado" : "Copiar código de verificación"}
      className="aia-cert__action"
    >
      {copied ? (
        <>
          <Check aria-hidden="true" style={{ color: "#357a50" }} /> ¡Copiado!
        </>
      ) : (
        <>
          <Copy aria-hidden="true" /> Copiar
        </>
      )}
    </button>
  );
}

/**
 * LA credencial en pantalla, en un solo componente. Lo usan la pantalla de fin de curso, la
 * vista previa, Mi Aula, el perfil, el progreso, la ficha del curso, la página pública de
 * verificación y los ejemplos de las landings. Si la credencial cambia de aspecto, se cambia
 * acá (y en `styles/certificate.css`) y cambia en todos lados.
 *
 * El PDF que se descarga NO sale de acá: lo compone el servidor con su plantilla
 * (`certificate-pdf.template.ts`). Esta tarjeta es su réplica a escala cuando hay ancho, y una
 * versión apilada cuando no. Lo que DICE el papel es uno solo: los dos lados llaman a
 * `credentialPaperText` de `@repo/shared`, y la firma es el mismo trazo compartido.
 */
export function CertificateCard({
  holderName,
  courseTitle,
  type,
  completedAt,
  issuedAt,
  code,
  variant = "full",
  codeActions,
  className,
}: CertificateCardProps) {
  const origin = useOrigin();
  const text = credentialPaperText({ type, issuedAt, completedAt });
  const { dates, signature } = text;

  const thumbnail = variant === "thumbnail";
  const example = variant === "example";
  const actions: CertificateCodeActions = thumbnail
    ? "none"
    : (codeActions ?? (example ? "none" : "full"));

  const name = holderName || "—";
  const course = courseTitle || "—";
  const sizes = {
    "--name-mm": fitSize(name.length, 42, 26, 24) * PT_TO_MM,
    "--course-mm": fitSize(course.length, 20, 15, 56) * PT_TO_MM,
    "--name-px": `${fitSize(name.length, 30, 22, 20)}px`,
    "--course-px": `${fitSize(course.length, 19, 16, 44)}px`,
  } as CSSProperties;

  // El QR lleva a la página pública, como el del papel. El del ejemplo lleva a la página del
  // ejemplo: su código no existe.
  const qrValue =
    origin && code ? `${origin}${example ? "/certificado" : credentialPublicPath(code)}` : null;
  const host = origin ? new URL(origin).host.replace(/^www\./, "") : FALLBACK_HOST;

  return (
    <div
      className={cn(
        "aia-cert",
        thumbnail ? "aia-cert--thumb pointer-events-none select-none" : "aia-cert--readable",
        className,
      )}
      aria-hidden={thumbnail ? true : undefined}
    >
      <div className="aia-cert__paper" style={{ ...PAPEL_CLARO, ...sizes }}>
        <div className="aia-cert__frame-outer" />
        <div className="aia-cert__frame-inner" />
        {example && (
          // La tarjeta de ejemplo circula suelta en capturas: sin la marca se confunde con
          // una credencial emitida de verdad.
          <span aria-hidden="true" className="aia-cert__watermark">
            Ejemplo
          </span>
        )}

        <div className="aia-cert__content">
          <div className="aia-cert__lockup">
            <img src={academiaIsotipo} alt="" />
            <span className="aia-cert__wordmark">
              Academ<span>IA</span>
            </span>
          </div>
          <p className="aia-cert__eyebrow">Academia de Inteligencia Artificial</p>
          <div className="aia-cert__kind">
            <span className="aia-cert__kind-rule" />
            <p className="aia-cert__kind-label">{text.label}</p>
            <span className="aia-cert__kind-rule" />
          </div>

          <div className="aia-cert__gap-top" />
          <p className="aia-cert__lead">{text.preamble}</p>
          <p className="aia-cert__holder">{name}</p>
          <div className="aia-cert__holder-rule" />
          <p className="aia-cert__lead">{text.achievement}</p>
          <p className="aia-cert__course">{course}</p>
          <div className="aia-cert__gap-bottom" />

          <div className="aia-cert__validation">
            <div
              className="aia-cert__sign"
              role="img"
              aria-label={`Firma: ${signature.name}`}
              // Es una constante del paquete compartido (contornos, sin datos de nadie): el
              // mismo SVG que imprime el PDF.
              dangerouslySetInnerHTML={{ __html: CREDENTIAL_SIGNATURE_SVG }}
            />
            <div className="aia-cert__rule aia-cert__rule--sign" />
            <p className="aia-cert__caption aia-cert__caption--sign">{signature.caption}</p>

            {code && (
              <div className="aia-cert__qr">
                {qrValue && (
                  <QRCodeSVG
                    value={qrValue}
                    level="M"
                    marginSize={0}
                    fgColor="#1F1A15"
                    bgColor="#FFFFFF"
                    title="Código QR de verificación"
                  />
                )}
              </div>
            )}

            <p className="aia-cert__date">{dates.value ?? "—"}</p>
            <div className="aia-cert__rule aia-cert__rule--date" />
            <p className="aia-cert__caption aia-cert__caption--date">{dates.label}</p>
            {dates.finishedNote && (
              <p className="aia-cert__note aia-cert__date-note">{dates.finishedNote}</p>
            )}

            {code && (
              <>
                <p className="aia-cert__caption aia-cert__caption--code">Código de verificación</p>
                <p className="aia-cert__code">{formatCertificateCode(code)}</p>
                {actions === "none" ? (
                  <p className="aia-cert__note aia-cert__foot">
                    Verificá en <strong>{host}/verificar</strong>
                  </p>
                ) : (
                  <div className="aia-cert__foot aia-cert__actions">
                    <CopyCodeButton code={code} />
                    {actions === "full" && (
                      <Link
                        to={credentialPublicPath(code)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aia-cert__action aia-cert__action--link"
                      >
                        Ver página pública <ExternalLink aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
